// src/pages/CheckoutPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag, MapPin, Phone, User, Mail,
  CreditCard, CheckCircle, AlertCircle, Tag, X, Sparkles,
} from 'lucide-react';
import {
  collection, addDoc, doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import { createShipment } from '../utils/shiprocket';
import { sendOrderConfirmation } from '../utils/notifications';
import { validateCoupon, getAutoApplyCoupon, recordCouponUsage } from '../utils/couponUtils';
import Loader from '../components/Loader';
import toast from 'react-hot-toast';

function groupByMerchant(cart) {
  return cart.reduce((groups, item) => {
    const mid = item.merchantId || 'couplecart';
    if (!groups[mid]) groups[mid] = [];
    groups[mid].push(item);
    return groups;
  }, {});
}

export default function CheckoutPage() {
  const { cart, cartTotal, user, clearCart } = useApp();

  const [loading,      setLoading]      = useState(false);
  const [orderPlaced,  setOrderPlaced]  = useState(false);
  const [orderId,      setOrderId]      = useState('');
  const [form,         setForm]         = useState({
    name:    user?.displayName || '',
    email:   user?.email       || '',
    phone: '', address: '', city: '', state: '', pincode: '',
  });
  const [errors,       setErrors]       = useState({});

  // ── Coupon state ──────────────────────────────────────────────
  const [couponInput,   setCouponInput]   = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { coupon, discount }
  const [couponError,   setCouponError]   = useState('');
  const [autoChecked,   setAutoChecked]   = useState(false);

  // ── Auto-apply coupons when cart loads ────────────────────────
  useEffect(() => {
    if (!user || autoChecked || cart.length === 0) return;
    setAutoChecked(true);
    getAutoApplyCoupon(cartTotal, user.uid).then((result) => {
      if (result) {
        setAppliedCoupon({ coupon: result.coupon, discount: result.discount });
        toast.success(`🎉 "${result.coupon.code}" auto-applied! You save ₹${result.discount}`);
      }
    });
  }, [user, cart, cartTotal, autoChecked]);

  // ── Computed totals ───────────────────────────────────────────
  const discount    = appliedCoupon?.discount || 0;
  const finalTotal  = Math.max(0, cartTotal - discount);

  // ── Guards ────────────────────────────────────────────────────
  if (!user) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-cream pt-20">
      <div className="text-4xl">🔒</div>
      <h2 className="font-display font-bold text-2xl text-gray-800">Please login to checkout</h2>
      <Link to="/login" className="bg-rose-500 text-white px-6 py-3 rounded-2xl hover:bg-rose-600 transition-colors">Login</Link>
    </div>
  );

  if (cart.length === 0 && !orderPlaced) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-cream pt-20">
      <div className="text-4xl">🛒</div>
      <h2 className="font-display font-bold text-2xl text-gray-800">Your cart is empty</h2>
      <Link to="/products" className="bg-rose-500 text-white px-6 py-3 rounded-2xl hover:bg-rose-600 transition-colors">Browse Gifts</Link>
    </div>
  );

  // ── Validation ────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Full name is required';
    if (!form.email.trim())   e.email   = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.phone.trim())   e.phone   = 'Phone is required';
    else if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ''))) e.phone = 'Enter a valid 10-digit number';
    if (!form.address.trim()) e.address = 'Address is required';
    if (!form.city.trim())    e.city    = 'City is required';
    if (!form.state.trim())   e.state   = 'State is required';
    if (!form.pincode.trim()) e.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(form.pincode)) e.pincode = 'Enter valid 6-digit pincode';
    return e;
  };

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: '' }));
  };

  // ── Apply coupon ──────────────────────────────────────────────
  const applyCoupon = async () => {
    if (!couponInput.trim()) { setCouponError('Enter a coupon code'); return; }
    setCouponLoading(true);
    setCouponError('');
    try {
      const result = await validateCoupon(couponInput.trim(), cartTotal, user.uid);
      if (result.valid) {
        setAppliedCoupon({ coupon: result.coupon, discount: result.discount });
        setCouponInput('');
        toast.success(`✅ Coupon applied! You save ₹${result.discount}`);
      } else {
        setCouponError(result.error);
      }
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
    setCouponInput('');
    toast('Coupon removed');
  };

  // ── Load Razorpay ─────────────────────────────────────────────
  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s   = document.createElement('script');
    s.src     = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  // ── Payment ───────────────────────────────────────────────────
  const handlePayment = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { toast.error('Payment gateway failed to load.'); setLoading(false); return; }

      const options = {
        key:         process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount:      Math.round(finalTotal * 100),
        currency:    'INR',
        name:        'CoupleCart',
        description: `Order for ${form.name}`,
        image:       '/logo192.png',
        prefill:     { name: form.name, email: form.email, contact: form.phone },
        notes:       { coupon: appliedCoupon?.coupon?.code || 'none', discount: discount },
        theme:       { color: '#f43f5e' },

        handler: async (response) => {
          try {
            const merchantGroups = groupByMerchant(cart);
            const merchantIds    = Object.keys(merchantGroups);

            // 1. Save main order
            const mainRef = await addDoc(collection(db, 'orders'), {
              userId:          user.uid,
              userEmail:       user.email,
              items:           cart,
              shippingAddress: form,
              subtotal:        cartTotal,
              discount:        discount,
              couponCode:      appliedCoupon?.coupon?.code || null,
              deliveryFee:     0,
              total:           finalTotal,
              paymentId:       response.razorpay_payment_id,
              paymentStatus:   'paid',
              status:          'confirmed',
              trackingStatus:  'Order Placed',
              merchantIds,
              awbCode:         null,
              courierName:     null,
              createdAt:       serverTimestamp(),
            });

            const mainOrderId = mainRef.id;
            const merchantOrderIds = [];

            // 2. Per-merchant sub-orders
            for (const [merchantId, items] of Object.entries(merchantGroups)) {
              const merchantTotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
              const moRef = await addDoc(collection(db, 'merchantOrders'), {
                orderId:         mainOrderId,
                merchantId,
                userId:          user.uid,
                userEmail:       user.email,
                items,
                shippingAddress: form,
                total:           merchantTotal,
                paymentId:       response.razorpay_payment_id,
                paymentStatus:   'paid',
                status:          'confirmed',
                trackingStatus:  'Order Placed',
                awbCode:         null,
                courierName:     null,
                createdAt:       serverTimestamp(),
              });
              merchantOrderIds.push(moRef.id);

              // Shiprocket (non-blocking)
              createShipment({
                orderId: moRef.id, name: form.name, address: form.address,
                city: form.city, pincode: form.pincode, state: form.state,
                email: form.email, phone: form.phone, items, total: merchantTotal,
              }).then(async (ship) => {
                const awb = ship?.payload?.awb_code || ship?.awb_code || null;
                if (awb) {
                  await updateDoc(doc(db, 'merchantOrders', moRef.id), {
                    awbCode: awb,
                    courierName: ship?.payload?.courier_name || null,
                    status: 'processing',
                  });
                }
              }).catch(() => {});
            }

            // 3. Update main order
            await updateDoc(doc(db, 'orders', mainOrderId), { merchantOrderIds });

            // 4. Record coupon usage
            if (appliedCoupon?.coupon) {
              recordCouponUsage(appliedCoupon.coupon.id, mainOrderId, user.uid, discount).catch(() => {});
            }

            // 5. Send confirmation email
            sendOrderConfirmation({
              id: mainOrderId, userEmail: user.email, items: cart,
              total: finalTotal, shippingAddress: form, paymentStatus: 'paid',
              paymentId: response.razorpay_payment_id,
            }).catch(() => {});

            clearCart();
            setOrderId(mainOrderId);
            setOrderPlaced(true);
          } catch (err) {
            console.error('Order save error:', err);
            toast.error(`Payment received but order save failed. Payment ID: ${response.razorpay_payment_id} — contact support.`);
          } finally {
            setLoading(false);
          }
        },

        modal: {
          ondismiss: () => { setLoading(false); toast('Payment cancelled. Cart is safe.'); },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setLoading(false);
        toast.error('Payment failed: ' + (resp.error?.description || 'Please try again.'));
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      toast.error('Failed to start payment. Please try again.');
      setLoading(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────
  if (orderPlaced) return (
    <div className="min-h-screen bg-cream flex items-center justify-center pt-20 p-4">
      <div className="bg-white rounded-3xl shadow-card p-10 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-green-500" />
        </div>
        <h2 className="font-display font-bold text-3xl text-gray-900 mb-2">Order Placed! 🎉</h2>
        <p className="text-gray-400 font-body mb-4">Thank you, {form.name}! Your gift is on its way 💕</p>
        {discount > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-3 mb-4">
            <p className="text-green-700 font-body text-sm font-semibold">
              🎊 You saved ₹{discount} with coupon {appliedCoupon?.coupon?.code}!
            </p>
          </div>
        )}
        <div className="bg-rose-50 rounded-2xl p-4 mb-4">
          <p className="text-xs text-gray-400 font-body">Order ID</p>
          <p className="font-body font-bold text-gray-800 text-sm mt-1 break-all">{orderId}</p>
        </div>
        <div className="flex flex-col gap-3">
          <Link to={`/track/${orderId}`}
            className="bg-rose-500 text-white py-3.5 rounded-2xl font-body font-semibold hover:bg-rose-600 transition-colors flex items-center justify-center gap-2">
            📦 Track My Order
          </Link>
          <Link to="/orders"
            className="border border-rose-200 text-rose-500 py-3.5 rounded-2xl font-body font-medium hover:bg-rose-50 transition-colors">
            View All Orders
          </Link>
          <Link to="/products" className="text-gray-400 hover:text-gray-600 py-2 text-sm font-body">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );

  // ── Form ───────────────────────────────────────────────────────
  const inputCls = (field) =>
    `w-full px-4 py-3.5 rounded-2xl border font-body text-gray-800 focus:outline-none transition-all text-sm ${
      errors[field] ? 'border-red-300 bg-red-50' : 'border-rose-200 focus:border-rose-400 bg-rose-50/30 focus:bg-white'
    }`;
  const withIcon = (field) =>
    `w-full pl-10 pr-4 py-3.5 rounded-2xl border font-body text-gray-800 focus:outline-none transition-all text-sm ${
      errors[field] ? 'border-red-300 bg-red-50' : 'border-rose-200 focus:border-rose-400 bg-rose-50/30 focus:bg-white'
    }`;
  const ErrMsg = ({ field }) => errors[field]
    ? <p className="text-red-500 text-xs mt-1 font-body flex items-center gap-1"><AlertCircle size={12} />{errors[field]}</p>
    : null;

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      {loading && <Loader fullPage />}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-display font-bold text-3xl text-gray-900 mb-8 mt-6">Checkout</h1>

        <div className="grid lg:grid-cols-5 gap-8">

          {/* ── Left — Form ── */}
          <div className="lg:col-span-3">
            <form onSubmit={handlePayment} className="space-y-6" noValidate>

              {/* Contact */}
              <div className="bg-white rounded-3xl p-6 shadow-soft">
                <h2 className="font-display font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <User size={20} className="text-rose-400" /> Contact Details
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input type="text" value={form.name} onChange={handleChange('name')}
                        placeholder="Your full name" autoComplete="name" className={withIcon('name')} />
                    </div>
                    <ErrMsg field="name" />
                  </div>
                  <div>
                    <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input type="email" value={form.email} onChange={handleChange('email')}
                        placeholder="you@example.com" autoComplete="email" className={withIcon('email')} />
                    </div>
                    <ErrMsg field="email" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Phone Number *</label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input type="tel" value={form.phone} onChange={handleChange('phone')}
                        placeholder="10-digit mobile number" autoComplete="tel" maxLength={10} className={withIcon('phone')} />
                    </div>
                    <ErrMsg field="phone" />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white rounded-3xl p-6 shadow-soft">
                <h2 className="font-display font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin size={20} className="text-rose-400" /> Delivery Address
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Street Address *</label>
                    <textarea value={form.address} onChange={handleChange('address')} rows={2}
                      placeholder="House/flat no., street, area, landmark"
                      className={`${inputCls('address')} resize-none`} />
                    <ErrMsg field="address" />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">City *</label>
                      <input type="text" value={form.city} onChange={handleChange('city')}
                        placeholder="City" autoComplete="address-level2" className={inputCls('city')} />
                      <ErrMsg field="city" />
                    </div>
                    <div>
                      <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">State *</label>
                      <input type="text" value={form.state} onChange={handleChange('state')}
                        placeholder="State" autoComplete="address-level1" className={inputCls('state')} />
                      <ErrMsg field="state" />
                    </div>
                    <div>
                      <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Pincode *</label>
                      <input type="text" value={form.pincode} onChange={handleChange('pincode')}
                        placeholder="6 digits" autoComplete="postal-code" maxLength={6} className={inputCls('pincode')} />
                      <ErrMsg field="pincode" />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Coupon Box ── */}
              <div className="bg-white rounded-3xl p-6 shadow-soft">
                <h2 className="font-display font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <Tag size={20} className="text-rose-400" /> Coupon / Promo Code
                </h2>

                {appliedCoupon ? (
                  /* Applied state */
                  <div className="flex items-center justify-between bg-green-50 border border-green-300 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-green-500 shrink-0" />
                      <div>
                        <p className="font-mono font-bold text-green-700 text-base">{appliedCoupon.coupon.code}</p>
                        <p className="text-green-600 font-body text-xs">
                          {appliedCoupon.coupon.type === 'percent'
                            ? `${appliedCoupon.coupon.value}% off`
                            : `₹${appliedCoupon.coupon.value} off`
                          } — You save ₹{appliedCoupon.discount}!
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={removeCoupon}
                      className="w-7 h-7 flex items-center justify-center rounded-xl hover:bg-red-100 transition-colors text-gray-400 hover:text-red-500">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  /* Input state */
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="text"
                          value={couponInput}
                          onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyCoupon())}
                          placeholder="Enter coupon code"
                          className="w-full pl-10 pr-4 py-3.5 rounded-2xl border border-rose-200 focus:border-rose-400 focus:bg-white bg-rose-50/30 font-mono text-sm focus:outline-none transition-all uppercase tracking-wider"
                          autoComplete="off"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={couponLoading || !couponInput.trim()}
                        className="px-6 py-3.5 bg-rose-500 hover:bg-rose-600 text-white rounded-2xl font-body font-semibold text-sm transition-all disabled:opacity-60 shrink-0"
                      >
                        {couponLoading ? (
                          <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        ) : 'Apply'}
                      </button>
                    </div>
                    {couponError && (
                      <p className="text-red-500 text-xs font-body flex items-center gap-1.5">
                        <AlertCircle size={13} /> {couponError}
                      </p>
                    )}
                    <p className="text-gray-400 font-body text-xs">
                      💡 Try <span className="font-mono font-semibold text-rose-400">WELCOME10</span> for 10% off your first order
                    </p>
                  </div>
                )}
              </div>

              {/* Pay button */}
              <button type="submit"
                className="w-full flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-4 rounded-2xl font-body font-bold text-lg transition-all shadow-rose">
                <CreditCard size={20} />
                Pay ₹{finalTotal.toLocaleString('en-IN')} via Razorpay
              </button>
              <p className="text-center text-xs text-gray-400 font-body">
                🔒 100% Secure · Razorpay · Free Delivery on All Orders
              </p>
            </form>
          </div>

          {/* ── Right — Summary ── */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-6 shadow-soft sticky top-24">
              <h2 className="font-display font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                <ShoppingBag size={20} className="text-rose-400" /> Order Summary
              </h2>

              {/* Items */}
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-3 items-center">
                    <img src={item.image} alt={item.name}
                      className="w-14 h-14 object-cover rounded-xl shrink-0 border border-rose-100" />
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-gray-800 font-medium line-clamp-1">{item.name}</p>
                      <p className="text-xs text-gray-400 font-body">Qty: {item.quantity}</p>
                    </div>
                    <span className="font-body font-semibold text-gray-900 text-sm shrink-0">
                      ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-rose-100 pt-4 space-y-2.5">
                <div className="flex justify-between text-sm font-body text-gray-500">
                  <span>Subtotal</span>
                  <span>₹{cartTotal.toLocaleString('en-IN')}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm font-body text-green-600 font-semibold">
                    <span className="flex items-center gap-1">
                      <Sparkles size={14} /> Coupon ({appliedCoupon?.coupon?.code})
                    </span>
                    <span>- ₹{discount.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-body text-gray-500">
                  <span>Delivery</span>
                  <span className="text-green-600 font-semibold">FREE 🎉</span>
                </div>
                <div className="flex justify-between font-display font-bold text-xl text-gray-900 pt-2 border-t border-rose-100">
                  <span>Total</span>
                  <span>₹{finalTotal.toLocaleString('en-IN')}</span>
                </div>
                {discount > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-2xl px-4 py-2.5 text-center">
                    <p className="text-green-700 font-body font-semibold text-sm">
                      🎊 You're saving ₹{discount} on this order!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
